import {
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ColumnApi,
  DomLayoutType,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  SortChangedEvent,
} from 'ag-grid-community';
import { Subject, Subscription } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { PaginationPageSizes } from 'src/app/constants';
import { StorageService } from 'src/app/shared/services/storage.service';
import { SharedService } from 'src/app/shared/shared.service';

export interface UrlParam {
  paramLabel: string;
  paramValue: string | number;
}

@Directive()
export abstract class ServerSideGridBase implements OnInit, OnChanges, OnDestroy {

  @Input() rowDataMain: any;
  @Input() rowData: Array<any>;
  @Input() tableConfigOptions: any;
  @Input() gridSearchText: any;
  @Input() isStoreParams: boolean;
  @Input() formSide: string;
  @Input() fromComponent: string;
  @Input() sectionName: string;

  @Output() getDataEvent = new EventEmitter();
  @Output() rowSelectedEvent = new EventEmitter();
  @Output() rowEditedEvent = new EventEmitter();
  @Output() rowActionEvent = new EventEmitter();

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  // Per-grid hooks (concrete subclass provides values / impl)
  abstract columnDefs: ColDef[];
  abstract frameworkComponents: any;
  protected abstract storageKey: string;
  protected abstract gridIdSuffix: string;
  protected abstract initialFilters: { [key: string]: string };
  protected abstract initialSorts: { [key: string]: string };
  protected abstract mapFilterModel(filterModel: any): void;
  protected abstract mapSortModel(sortModel: any): void;

  // Optional: extra static query params (e.g. wf-logs sends include-related=true)
  protected extraStaticParams: UrlParam[] = [];

  // Shared state
  idString = '';
  gridApi!: GridApi;
  gridColumnApi!: ColumnApi;
  gridOptions!: GridOptions;
  rowSelection: 'single' | 'multiple' = 'single';
  selectedRows: any;
  domLayout!: DomLayoutType;
  paginationPageSize = 10;
  paginationPageSizeNew: any;
  // Page size / dropdown options. Seeded from tableConfigOptions in
  // operateTableConfigs(); fall back to the shared defaults when not supplied.
  selectedPageSizeValue = 10;
  paginationPageSizes: number[] = PaginationPageSizes;
  // When true, the page-size dropdown is hidden (nav controls/summary remain);
  // useful with a single fixed page size. Seeded from tableConfigOptions; the
  // grid still loads at the configured page size.
  hidePageSizeSelector = false;
  // When true, the entire pagination bar is hidden (dropdown + nav + summary);
  // the grid shows only the first page at the configured page size. Seeded from
  // tableConfigOptions.
  hidePagination = false;
  startIndex = 0;
  endIndex = 0;
  currentPage = 1;
  style: any = { width: '100%', height: '100%' };

  defaultColDef: ColDef = {
    sortable: true,
    comparator: () => 0,
    filter: false,
    headerTooltip: '',
    cellDataType: false,
    flex: 1,
    minWidth: 140,
  };

  urlParams: { searchText: string; filters: any; sorts: any } = {
    searchText: '',
    filters: {},
    sorts: {},
  };

  storedParams: any = {
    filterModel: {},
    sortModel: [],
    searchText: '',
  };

  // Coalesces sync bursts of getGridData() into a single emit per microtask turn
  private dataRequest$ = new Subject<void>();
  private dataRequestSub?: Subscription;

  // True while we programmatically restore stored filter/sort state — suppresses
  // the filterChanged/sortChanged events that ag-grid fires as a side effect, so
  // we only emit one consolidated request afterwards.
  protected isRestoringState = false;

  get totalRecordsCount(): number {
    return this.rowDataMain && this.rowDataMain.total_count ? this.rowDataMain.total_count : 0;
  }

  get totalPages(): number {
    return Math.ceil((this.totalRecordsCount / (this.paginationPageSize || 1)) || 1);
  }

  constructor(
    protected storageService: StorageService,
    protected sharedService: SharedService,
  ) { }

  ngOnInit(): void {
    this.idString = this.sharedService.createIdString(this.fromComponent, this.sectionName, this.gridIdSuffix);
    this.urlParams.filters = { ...this.initialFilters };
    this.urlParams.sorts = { ...this.initialSorts };
    this.operateTableConfigs();
    // operateTableConfigs() may have applied a parent-supplied page size; mirror
    // it onto paginationPageSize so the grid actually starts on that size.
    this.paginationPageSize = Number(this.selectedPageSizeValue) || 10;
    this.selectedPageSizeValue = this.paginationPageSize;
    this.gridOptions = {
      sortingOrder: ['asc', 'desc'],
      pagination: true,
      suppressPaginationPanel: true,
      paginationPageSize: this.paginationPageSize,
      animateRows: true,
      tooltipShowDelay: 0,
      tooltipInteraction: true,
      context: { componentParent: this },
    };

    this.dataRequestSub = this.dataRequest$
      .pipe(auditTime(0))
      .subscribe(() => this.emitDataRequest());
  }

  ngOnDestroy(): void {
    this.dataRequestSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gridSearchText'] && !changes['gridSearchText'].firstChange && this.gridApi) {
      this.urlParams.searchText = this.gridSearchText;
      this.storedParams.searchText = this.gridSearchText;
      this.currentPage = 1;
      this.requestData();
    }
    if (this.rowDataMain || this.rowData) {
      this.updatePagination();
    }
  }

  protected operateTableConfigs(): void {
    this.domLayout =
      this.tableConfigOptions && this.tableConfigOptions.domLayoutType
        ? this.tableConfigOptions.domLayoutType
        : 'autoHeight';
    // Optional page-size config. `pageSizes` overrides the dropdown options;
    // `selectedPageSize` (or the single entry of `pageSizes`) sets the starting
    // size. Both fall back to the shared defaults when not supplied.
    if (this.tableConfigOptions && this.tableConfigOptions.pageSizes && this.tableConfigOptions.pageSizes.length) {
      this.paginationPageSizes = this.tableConfigOptions.pageSizes;
      this.selectedPageSizeValue = this.tableConfigOptions.pageSizes[0];
    }
    if (this.tableConfigOptions && this.tableConfigOptions.selectedPageSize) {
      this.selectedPageSizeValue = this.tableConfigOptions.selectedPageSize;
    }
    if (this.tableConfigOptions && this.tableConfigOptions.hidePageSizeSelector) {
      this.hidePageSizeSelector = true;
    }
    if (this.tableConfigOptions && this.tableConfigOptions.hidePagination) {
      this.hidePagination = true;
    }
    if (this.tableConfigOptions && this.tableConfigOptions.columns && this.tableConfigOptions.columns.length) {
      this.tableConfigOptions.columns.forEach((item: any) => {
        this.columnDefs.forEach(column => {
          if (item.headerName === column.headerName) {
            column.hide = item.hide;
            // Optional per-column sortability from the parent. Checked against
            // undefined (not truthiness) so a parent can pass `sortable: false`.
            if (item.sortable !== undefined) {
              column.sortable = item.sortable;
            }
            // Optional per-column filter from the parent. Checked against
            // undefined so a parent can pass `filter: false` to disable it
            // (or a filter component name to override the column's default).
            if (item.filter !== undefined) {
              column.filter = item.filter;
            }
          }
        });
      });
    }
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.hideOverlay();
    this.checkStoredParamsAndCallGridDataFn();
    window.setTimeout(() => params.api.sizeColumnsToFit(), 500);
  }

  protected checkStoredParamsAndCallGridDataFn(): void {
    const stored = this.storageService.getGridParams(this.formSide, this.fromComponent, this.storageKey);
    const filterLen = stored && stored.filterModel ? Object.keys(stored.filterModel).length : 0;
    const sortLen = stored && stored.sortModel ? stored.sortModel.length : 0;
    if (stored && (filterLen || sortLen)) {
      this.applyStoredParams(stored);
    } else {
      this.requestData();
    }
  }

  protected applyStoredParams(stored: any): void {
    this.isRestoringState = true;
    this.storedParams.filterModel = stored.filterModel || {};
    this.storedParams.sortModel = stored.sortModel || [];

    if (stored.filterModel) {
      this.gridApi.setFilterModel(stored.filterModel);
      this.mapFilterModel(stored.filterModel);
    }
    if (stored.sortModel && stored.sortModel.length) {
      this.gridColumnApi.applyColumnState({ state: stored.sortModel });
      this.mapSortModel(stored.sortModel);
    }

    this.isRestoringState = false;
    this.requestData();
  }

  onFilterChanged(e: FilterChangedEvent): void {
    if (this.isRestoringState) return;
    const filterModel = e.api.getFilterModel();
    this.storedParams.filterModel = filterModel;
    if (filterModel) {
      this.mapFilterModel(filterModel);
      this.currentPage = 1;
      this.requestData();
    }
  }

  onSortChanged(e: SortChangedEvent): void {
    if (this.isRestoringState) return;
    const state = e.columnApi.getColumnState();
    this.storedParams.sortModel = state;
    if (state.length) {
      this.mapSortModel(state);
      this.currentPage = 1;
      this.requestData();
    }
  }

  onRowSelected(_event?: any): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    this.selectedRows = selectedNodes.map(node => node.data);
    this.rowSelectedEvent.emit(this.selectedRows);
  }

  onRowDoubleClicked(event: any): void {
    this.rowEditedEvent.emit(event.data);
  }

  onResize(_event?: any): void {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  onPaginationCompUpdate(event: any): void {
    if (!event) return;
    switch (event.action) {
      case 'onPageSizeChanged':
        this.paginationPageSizeNew = Number(event.value);
        this.paginationPageSize = Number(this.paginationPageSizeNew);
        this.gridApi.paginationSetPageSize(this.paginationPageSize);
        this.currentPage = 1;
        this.requestData();
        break;
      case 'firstPageClick':
        this.currentPage = 1;
        this.gridApi.paginationGoToPage(1);
        this.requestData();
        break;
      case 'prevPageClick':
        this.currentPage--;
        this.gridApi.paginationGoToPage(this.currentPage);
        this.requestData();
        break;
      case 'nextPageClick':
        this.currentPage++;
        this.gridApi.paginationGoToPage(this.currentPage);
        this.requestData();
        break;
      case 'lastPageClick':
        this.currentPage = this.totalPages;
        this.gridApi.paginationGoToPage(this.currentPage);
        this.requestData();
        break;
    }
  }

  protected requestData(): void {
    this.dataRequest$.next();
  }

  private emitDataRequest(): void {
    const zeroBasedPage = this.currentPage > 0 ? this.currentPage - 1 : 0;
    this.startIndex = zeroBasedPage * this.paginationPageSize + 1;

    const urlParams: UrlParam[] = [
      ...this.extraStaticParams,
      { paramLabel: 'page', paramValue: this.currentPage },
      { paramLabel: 'per-page', paramValue: this.paginationPageSize },
    ];

    Object.keys(this.urlParams.filters).forEach(key => {
      const value = this.urlParams.filters[key];
      if (`${value}`) {
        urlParams.push({ paramLabel: key, paramValue: `${value}` });
      }
    });

    Object.keys(this.urlParams.sorts).forEach(key => {
      const value = `${this.urlParams.sorts[key]}`;
      if (value && value !== 'null') {
        urlParams.push(
          { paramLabel: 'sort-by', paramValue: key },
          { paramLabel: 'sort-order', paramValue: value === 'asc' ? 'ASCENDING' : 'DESCENDING' },
        );
      }
    });

    if (this.urlParams.searchText) {
      urlParams.push({ paramLabel: 'search-term', paramValue: this.urlParams.searchText });
    }

    if (this.isStoreParams) {
      this.storageService.saveGridParams(this.storedParams, this.formSide, this.fromComponent, this.storageKey);
    }

    this.getDataEvent.emit({ urlParams });
  }

  protected updatePagination(): void {
    this.endIndex = this.startIndex + this.paginationPageSize - 1;
    if (this.totalRecordsCount < this.endIndex) {
      this.endIndex = this.totalRecordsCount;
    }
    if (!this.endIndex) {
      this.startIndex = 0;
    }
  }
}
